// Portfolio Administration v1.0 PR 7 — Row 3 (Operational Health).

import type { PortfolioOperationalHealthMetrics } from "@/api/portfolioAdmin/pr7Types";
import {
  MetricCard,
  MetricsCardRow,
  formatPercent,
} from "./MetricsCards";
import { Card } from "@/design-system/components/ui";

export function OperationalHealthRow({
  data,
}: {
  data: PortfolioOperationalHealthMetrics;
}) {
  const sev = data.openCalendarBySeverity;
  const totalOpen = Object.values(sev).reduce((a, b) => a + b, 0);
  return (
    <MetricsCardRow
      title="Operational Health"
      testId="metrics-row-operational-health"
    >
      <MetricCard
        label="Insurance compliance"
        value={formatPercent(data.insuranceComplianceRate)}
        caption="% loans with all required policies current"
        testId="card-insurance-compliance"
      />
      <MetricCard
        label="UCC compliance"
        value={formatPercent(data.uccComplianceRate)}
        caption="% UCC-1s active and >90 days from lapse"
        testId="card-ucc-compliance"
      />
      <MetricCard
        label="Vendor program compliance"
        value={
          data.vendorProgramComplianceRate === 0
            ? "—"
            : formatPercent(data.vendorProgramComplianceRate)
        }
        caption={
          data.vendorProgramComplianceRate === 0
            ? "Catalog seeded; per-loan assignment surface pending"
            : "% required vendor slots filled"
        }
        testId="card-vendor-compliance"
      />
      <Card
        className="flex flex-col gap-space-1 p-space-3"
        data-testid="card-setup-cohorts"
      >
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Setup checklist completion
        </span>
        <table className="text-small">
          <tbody>
            <tr>
              <td className="pr-space-2 text-gray-mid">0–30 days old</td>
              <td className="font-medium">
                {formatPercent(data.setupCompletion.rateAge0To30)}
              </td>
            </tr>
            <tr>
              <td className="pr-space-2 text-gray-mid">30–60 days old</td>
              <td className="font-medium">
                {formatPercent(data.setupCompletion.rateAge30To60)}
              </td>
            </tr>
            <tr>
              <td className="pr-space-2 text-gray-mid">60–90 days old</td>
              <td className="font-medium">
                {formatPercent(data.setupCompletion.rateAge60To90)}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card
        className="flex flex-col gap-space-1 p-space-3"
        data-testid="card-calendar-by-severity"
      >
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Open Calendar items
        </span>
        <span className="font-display font-bold text-h3 text-onyx leading-none">
          {totalOpen}
        </span>
        <div className="flex flex-wrap gap-space-1 text-small">
          {Object.entries(sev).map(([k, v]) => (
            <span
              key={k}
              className="px-space-2 py-[2px] border border-border-light rounded-sm bg-white-pure"
            >
              {k}: {v}
            </span>
          ))}
          {totalOpen === 0 && (
            <span className="text-gray-mid">No open compliance items.</span>
          )}
        </div>
      </Card>

      <MetricCard
        label="Avg time-to-resolve (compliance)"
        value={`${data.averageTimeToResolveComplianceDays.toFixed(1)} d`}
        caption="Days from due date to completion"
        testId="card-avg-resolve"
      />

      <MetricCard
        label="Backup Servicer ready"
        value={data.backupServicerTransitionReady ? "✓" : "✗"}
        caption={
          data.backupServicerTransitionReady
            ? "Transition readiness OK"
            : "Audit / re-confirm required"
        }
        testId="card-backup-servicer"
      />
    </MetricsCardRow>
  );
}
